<template>
  <v-container
    class="ma-0 pa-0 white"
    fluid
    fill-height
  >
    <v-layout>
      <v-flex>
        <fullpage-loader
          message="טוען ספקים..."
          v-if="!event"
        ></fullpage-loader>

        <partner
          :comp-id="compId"
          v-else-if="partnerId"
        />

        <div v-else>
          <v-data-table
            :headers="headers.filter(item => shouldShow(item.roles, user))"
            :items="partners"
            item-key="_id"
            hide-actions
            must-sort
          >
            <template v-slot:items="props">
              <td class="font-weight-bold"><a @click="showPartner(props.item._id)">{{ props.item.name }}</a></td>
              <td class="text-xs-right">{{ partnerTypesText(props.item) }}</td>
              <td class="text-xs-right">
                <a
                  class="check-link"
                  @click.stop="openCheckDialog(props.item)"
                >{{ getCheckLabel(props.item) }}</a>
              </td>
              <td
                class="text-xs-left"
                width="30"
                v-if="shouldShow(['ADMIN', 'EVENT_MANAGER'], user)"
              >
                <v-btn
                  small
                  icon
                  class="ml-0 mr-0 mt-1"
                  :loading="removing[props.item._id]"
                  @click="remove(props.item._id)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </td>
            </template>
          </v-data-table>

          <v-btn
            small
            @click="add"
            class="mt-3"
            v-if="shouldShow(['ADMIN', 'EVENT_MANAGER'], user)"
          >
            <v-icon>add</v-icon> הוספת ספק
          </v-btn>
        </div>
      </v-flex>
    </v-layout>

    <partner-check-dialog
      v-model="checkDialog"
      :partner="selectedPartner"
      :event-id="eventId"
      @checked="onPartnerChecked"
    />

  </v-container>
</template>

<script>
import FullpageLoader from "../../FullpageLoader.vue";
import Partner from "./Partners/Partner.vue";
import PartnerCheckDialog from "../PartnerCheckDialog.vue";
import generateId from "@/idGenerator";
import shouldShow from "@/utils/shouldShow";
import { getApi } from "@/api";
import { differenceInCalendarDays } from "date-fns";

export default {
  data() {
    return {
      loading: false,
      removing: {},
      compId: null,
      checkDialog: false,
      selectedPartner: null,
      headers: [
        {
          text: "שם",
          align: "right",
          value: "name"
        },
        { text: "סוג", align: "right", value: "types" },
        { text: "בדיקה", align: "right", value: "lastValidation" },
        {
          text: "הסרה",
          align: "left",
          sortable: false,
          roles: ["ADMIN", "EVENT_MANAGER"]
        }
      ]
    };
  },

  computed: {
    partnerId() {
      return this.$store.getters["partners/activePartnersIds"][this.compId];
    },
    event() {
      return this.$store.state.entities.events[this.eventId];
    },
    partners() {
      // Check if we're in כולם mode (multiple events)
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;
      
      if (isAllEventsMode) {
        // In כולם mode, get partners from all events in the ARAN
        console.log('Partners computed - כולם mode, eventIdsToShow:', eventIdsToShow);
        
        const allPartners = [];
        const seenPartnerIds = new Set(); // To avoid duplicates
        
        eventIdsToShow.forEach(eventId => {
          const event = this.$store.state.entities.events[eventId];
          if (event && event.partners) {
            event.partners.forEach(partnerId => {
              if (!seenPartnerIds.has(partnerId)) {
                const partner = this.$store.state.entities.partners[partnerId];
                if (partner) {
                  allPartners.push(partner);
                  seenPartnerIds.add(partnerId);
                }
              }
            });
          }
        });
        
        console.log('Partners computed - כולם mode, found partners:', allPartners.length);
        return allPartners;
      } else {
        // Single event mode - show partners from current event only
        if (this.event && this.event.partners) {
          return this.event.partners.map(item => {
            return this.$store.state.entities.partners[item];
          }).filter(Boolean);
        } else {
          return [];
        }
      }
    },
    activePartnerId: {
      get() {
        return this.$store.getters["partners/activePartnersIds"][this.compId];
      },
      set(payload) {
        this.$store.commit("partners/SET_ACTIVE_PARTNER_ID", {
          compId: this.compId,
          payload
        });
      }
    },
    user() {
      return this.$store.getters["user/user"];
    }
  },
  methods: {
    shouldShow,
    partnerTypesText(partner) {
      if (!partner || !partner.types || !partner.types.length) {
        return "לא ידוע";
      }
      return partner.types
        .map(item => {
          const type =
            typeof item === "string"
              ? this.$store.state.entities.partnerTypes[item]
              : item;
          return type ? type.title : null;
        })
        .filter(Boolean)
        .join(", ") || "לא ידוע";
    },
    getCheckLabel(partner) {
      if (!partner || !partner.lastValidation) {
        return "אף פעם";
      }
      const days = differenceInCalendarDays(
        new Date(),
        new Date(partner.lastValidation)
      );
      if (isNaN(days)) {
        return "אף פעם";
      }
      return String(Math.max(0, days));
    },
    openCheckDialog(partner) {
      this.selectedPartner = partner;
      this.checkDialog = true;
    },
    onPartnerChecked() {
      this.$forceUpdate();
    },
    showPartner(id) {
      this.activePartnerId = id;
    },
    add() {
      // Check if we're in כולם mode (multiple events)
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;

      if (isAllEventsMode) {
        // Add partner to all events in the ARAN
        console.log('Partners add - adding to all events:', eventIdsToShow);
        
        // For partners, we need to add the partner to each event
        // This will be handled in the partners dialog
        this.setEventCountriesToUrl();
        this.$store.commit("events/TOGGLE_EVENT_PARTNERS_DIALOG", true);
        // Store the event IDs for the dialog to use
        this.$store.commit("events/SET_PARTNERS_FOR_ALL_EVENTS", {
          eventIds: eventIdsToShow
        });
      } else {
        // Add partner to single event (normal mode)
        this.setEventCountriesToUrl();
        this.$store.commit("events/TOGGLE_EVENT_PARTNERS_DIALOG", true);
      }
    },
    remove(partnerId) {
      this.$confirm("האם לבטל את הקישור של הספק לאירוע?").then(res => {
        if (!res) {
          return;
        } else {
          this.removeConfirmed(partnerId);
        }
      });
    },
    removeConfirmed(partnerId) {
      let eventId = this.eventId;
      this.removing[partnerId] = true;
      getApi()
        .then(api => {
          let params = {
            partnerId
          };
          return api.patch(`/events/${eventId}/remove-partner`, params);
        })
        .then(data => {})
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.removing[partnerId] = false;
        });
    },
    setEventCountriesToUrl() {
      let countries = this.event.countries.map(item => {
        if (item._id) {
          return item._id;
        } else {
          return item;
        }
      }).join(',');
      let query = Object.assign({}, this.$route.query, {
        countries
      });
      this.$router.replace({ query });
    }
  },
  mounted() {
    this.compId = generateId();
  },

  watch: {
    // Watch for event IDs to show changes (כולם mode)
    '$store.state.events.eventIdsToShow': {
      handler(newVal, oldVal) {
        console.log('Partners watcher - eventIdsToShow changed from:', oldVal, 'to:', newVal);
        // Force re-computation of partners
        this.$forceUpdate();
      },
      deep: true
    },
    // Watch for showAllEvents changes (כולם toggle)
    '$store.state.events.showAllEvents': {
      handler(newShowAll, oldShowAll) {
        console.log('Partners watcher - showAllEvents changed from:', oldShowAll, 'to:', newShowAll);
        // Force re-computation of partners
        this.$forceUpdate();
      }
    },
    // Watch for route changes to refetch data when switching events
    '$route.params.id': {
      handler(newEventId, oldEventId) {
        console.log('Partners watcher - route changed from:', oldEventId, 'to:', newEventId);
        if (newEventId && newEventId !== oldEventId) {
          // Force re-computation of partners
          this.$forceUpdate();
        }
      },
      immediate: true
    }
  },

  props: ["eventId"],

  components: {
    FullpageLoader,
    Partner,
    PartnerCheckDialog
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.customer-container .v-toolbar__content {
  padding: 0 !important;
}
.check-link {
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/events/modules/Partners.vue